import React, { useMemo } from "react";
import "./Partners.css";
import { useTranslation } from "react-i18next";
import { usePageContent } from "../../../../hooks/usePublicQueries";
import { resolveMediaUrl } from "../../../../utils/media";

import p1 from "../../../../assets/images/home/companyprofile/p1.png";
import p2 from "../../../../assets/images/home/companyprofile/p2.png";
import p3 from "../../../../assets/images/home/companyprofile/p3.png";
import p4 from "../../../../assets/images/home/companyprofile/p4.png";

const fallbackLogos = [p1, p2, p3, p4];

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalescePartnersPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalescePartnersPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergePartnersWrapper = (node, into) => {
    const wrap = node?.partners ?? node?.Partners;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergePartnersWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergePartnersWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergePartnersWrapper(base, base);
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

/** Normalize object keys so API `bottomtext` matches `bottomText`. */
const normKey = (k) => String(k || "").toLowerCase().replace(/[_\s-]+/g, "");

/** First non-empty string for keys tried in order (case / separator insensitive). */
const pickPayloadString = (payload, ...keyCandidates) => {
  if (!payload || typeof payload !== "object") return "";
  const byNorm = {};
  for (const [k, v] of Object.entries(payload)) {
    byNorm[normKey(k)] = v;
  }
  for (const key of keyCandidates) {
    const v = byNorm[normKey(key)];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const resolvePartnerSlot = (payload, index) => {
  const i = String(index);
  const candidates = [
    payload?.[`partnerLogo${i}`],
    payload?.[`PartnerLogo${i}`],
    payload?.[`partner_logo_${i}`],
    payload?.[`partnerlogo${i}`],
    payload?.[`item${i}Image`],
    payload?.[`item${i}image`],
    payload?.[`item${i}MediaUrl`],
    payload?.[`item${i}mediaUrl`],
    payload?.[`item${i}Logo`],
    payload?.[`item${i}logo`],
    payload?.[`item${i}Media`],
    payload?.[`item${i}media`],
    payload?.[`logo${i}`],
    payload?.[`partner${i}`],
    payload?.[`partner${i}Image`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  if (payload && typeof payload === "object") {
    const want = normKey(`partnerLogo${i}`);
    for (const [k, v] of Object.entries(payload)) {
      if (normKey(k) === want) {
        const resolved = resolveMediaUrl(v);
        if (resolved) return resolved;
      }
    }
  }
  return "";
};

const collectPartnerLogos = (payload) => {
  const out = [];
  for (let n = 1; n <= 20; n++) {
    const url = resolvePartnerSlot(payload, n);
    if (url) out.push(url);
  }
  return out;
};

const hasMeaningfulPartnersPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (
    pickPayloadString(c, "bottomLine1", "bottomline1") ||
    pickPayloadString(c, "bottomLine2", "bottomline2") ||
    pickPayloadString(c, "bottomText", "bottomtext") ||
    pickPayloadString(c, "footerTagline", "footertagline")
  ) {
    return true;
  }
  return collectPartnerLogos(c).length > 0;
};

const Partners = ({ content }) => {
  const { i18n } = useTranslation();
  const shouldFetch = !(content && typeof content === "object");
  const { data: aboutPage } = usePageContent("about", i18n.language, { enabled: shouldFetch });

  const apiContent = useMemo(() => {
    if (!shouldFetch) return null;
    return findPageSection(aboutPage, "partners") || null;
  }, [aboutPage, shouldFetch]);

  const view = useMemo(() => {
    const source = content ?? apiContent;
    const payload = coalescePartnersPayload(source);
    if (!hasMeaningfulPartnersPayload(payload) || !payload) {
      return {
        bottomText: "",
        logos: fallbackLogos.map((fb) => ({ src: fb, fallback: fb })),
      };
    }

    const apiLogos = collectPartnerLogos(payload);
    const logos =
      apiLogos.length > 0
        ? apiLogos.map((src, idx) => ({ src, fallback: fallbackLogos[idx % fallbackLogos.length] }))
        : fallbackLogos.map((fb) => ({ src: fb, fallback: fb }));

    const b1 = pickPayloadString(payload, "bottomLine1", "bottomline1");
    const b2 = pickPayloadString(payload, "bottomLine2", "bottomline2");
    const legacyBottom = b1 && b2 ? `${b1} ${b2}`.trim() : pickString(b1, b2);
    const bottomText = pickString(
      pickPayloadString(payload, "bottomText", "bottomtext"),
      pickPayloadString(payload, "footerTagline", "footertagline"),
      legacyBottom
    );

    return { bottomText, logos };
  }, [content, apiContent]);

  return (
    <section className="partners">
      <div className="partners-bottom">
        {view.bottomText ? <p>{view.bottomText}</p> : null}

        <div className="partners-logos">
          {view.logos.map((logo, i) => (
            <img
              src={logo.src}
              key={`${logo.src}-${i}`}
              alt=""
              onError={(e) => {
                if (e.currentTarget.src !== logo.fallback) {
                  e.currentTarget.src = logo.fallback;
                }
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Partners;
